import { generate } from "@suaveplan/qrcode";

export const QR_STUDIO_DEFAULT_VALUE = "https://qrcode.suaveplan.com";
const QUIET_ZONE_MODULES = 4;
const SVG_PIXELS_PER_MODULE = 10;

export type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";

export interface QrStudioSettings {
    readonly background: string;
    readonly color: string;
    readonly errorCorrectionLevel: ErrorCorrectionLevel;
    readonly exportSize: number;
    readonly value: string;
}

export const QR_STUDIO_DEFAULTS: QrStudioSettings = {
    background: "#ffffff",
    color: "#0d0f0d",
    errorCorrectionLevel: "M",
    exportSize: 1024,
    value: QR_STUDIO_DEFAULT_VALUE,
};

export function createQrCode(settings: QrStudioSettings) {
    const result = generate(settings.value.trim(), {
        background: settings.background,
        color: settings.color,
        errorCorrectionLevel: settings.errorCorrectionLevel,
        margin: QUIET_ZONE_MODULES,
    });

    return {
        ...result,
        svg: renderCrispSvg(result.matrix, settings),
    };
}

/** Render QR modules as one integer-coordinate path to prevent raster seams. */
function renderCrispSvg(
    matrix: readonly (readonly boolean[])[],
    settings: Pick<QrStudioSettings, "background" | "color">
): string {
    const moduleCount = matrix.length + QUIET_ZONE_MODULES * 2;
    const svgPixels = moduleCount * SVG_PIXELS_PER_MODULE;
    const path: string[] = [];

    for (let row = 0; row < matrix.length; row++) {
        for (let column = 0; column < matrix.length; column++) {
            if (matrix[row]?.[column]) {
                const x = column + QUIET_ZONE_MODULES;
                const y = row + QUIET_ZONE_MODULES;
                path.push(`M${x} ${y}h1v1H${x}z`);
            }
        }
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${moduleCount} ${moduleCount}" width="${svgPixels}" height="${svgPixels}" shape-rendering="crispEdges"><rect width="${moduleCount}" height="${moduleCount}" fill="${escapeXml(settings.background)}"/><path d="${path.join("")}" fill="${escapeXml(settings.color)}"/></svg>`;
}

export function svgBlob(svg: string): Blob {
    return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
}

export function downloadBlob(blob: Blob, filename: string): void {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function pngBlob(
    matrix: readonly (readonly boolean[])[],
    settings: Pick<QrStudioSettings, "background" | "color">,
    size: number
): Promise<Blob> {
    const moduleCount = matrix.length + QUIET_ZONE_MODULES * 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("Canvas is unavailable");

    context.fillStyle = settings.background;
    context.fillRect(0, 0, size, size);
    context.fillStyle = settings.color;
    for (let row = 0; row < matrix.length; row++) {
        for (let column = 0; column < matrix.length; column++) {
            if (matrix[row]?.[column]) {
                const left = moduleEdge(column + QUIET_ZONE_MODULES, moduleCount, size);
                const top = moduleEdge(row + QUIET_ZONE_MODULES, moduleCount, size);
                const right = moduleEdge(column + QUIET_ZONE_MODULES + 1, moduleCount, size);
                const bottom = moduleEdge(row + QUIET_ZONE_MODULES + 1, moduleCount, size);
                context.fillRect(left, top, right - left, bottom - top);
            }
        }
    }

    return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) =>
                blob === null
                    ? reject(new Error("PNG export could not complete"))
                    : resolve(blob),
            "image/png"
        );
    });
}

/** Maps module edges to contiguous whole pixels at an exact requested size. */
function moduleEdge(index: number, moduleCount: number, size: number): number {
    return Math.round((index * size) / moduleCount);
}

function escapeXml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&apos;");
}
